import { ImageOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import {
  FeedbackSnackbar,
  InformationPage,
  PageSections,
  ResourceState,
  SectionCard,
  designTokens,
  normalizeBrandThemeSetting,
  resolveBrandTheme,
  type BrandThemePresetID,
} from '../../../design-system';
import { AppearanceEditor } from '../components/AppearanceEditor';
import { OrganizationLogoControl } from '../components/OrganizationLogoControl';
import { brandThemeKey, normalizeOrganizationSettings, validHexColor } from '../model/organization';
import { errorCode } from '../model/resource';
import type { TenantSettingsPageProps } from '../model/types';
import { useOrganizationWorkspace } from '../model/useOrganizationWorkspace';
import { useTenantSettingsRoute } from '../model/useTenantSettingsRoute';
export function AppearanceSettingsPage({
  port,
  route,
  fixtureMode,
  access,
  onOrganizationChanged,
}: TenantSettingsPageProps) {
  const location = useTenantSettingsRoute('admin-appearance', route);
  const canWrite =
    access.scopes.includes('tenant.settings.read') &&
    !access.readOnly &&
    access.scopes.includes('tenant.settings.write');
  const organizationWorkspace = useOrganizationWorkspace(port, fixtureMode, onOrganizationChanged);
  const { resource, draft, baseline, setDraft, setBaseline } = organizationWorkspace;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const theme =
    draft.brandTheme.presetId === 'custom-v1' ? draft.brandTheme : normalizeBrandThemeSetting(draft.brandTheme);
  const resolved = resolveBrandTheme(theme);
  const dirty = brandThemeKey(draft.brandTheme) !== brandThemeKey(baseline.brandTheme);
  const valid =
    theme.presetId !== 'custom-v1' || (validHexColor(theme.primaryColor) && validHexColor(theme.navigationColor) && (theme.accentColor === undefined || validHexColor(theme.accentColor)));
  const selectPreset = (presetId: BrandThemePresetID) => {
    setError(undefined);
    setNotice(undefined);
    setDraft((current) => {
      const currentTheme = resolveBrandTheme(current.brandTheme);
      return {
        ...current,
        brandTheme: presetId === 'custom-v1'
          ? { presetId, primaryColor: currentTheme.primaryColor, navigationColor: currentTheme.navigationColor, accentColor: currentTheme.accentColor, surfaceTone: current.brandTheme.surfaceTone }
          : { presetId, surfaceTone: current.brandTheme.surfaceTone },
      };
    });
  };
  const save = () => {
    if (!dirty || !valid || !canWrite || saving) return;
    setSaving(true);
    setError(undefined);
    setNotice(undefined);
    const normalizedTheme = normalizeBrandThemeSetting(draft.brandTheme);
    const request = fixtureMode
      ? Promise.resolve(
          normalizeOrganizationSettings({
            ...baseline,
            brandTheme: normalizedTheme,
            version: baseline.version + 1,
            updatedAt: new Date().toISOString(),
          }),
        )
      : port.updateOrganizationBrandTheme(normalizedTheme, baseline.version);
    void request
      .then(
        (settings) => {
          const normalized = normalizeOrganizationSettings(settings);
          setDraft((current) => ({
            ...normalized,
            displayName: current.displayName,
          }));
          setBaseline(normalized);
          onOrganizationChanged(normalized);
          setNotice('브랜드 테마를 저장했습니다.');
        },
        (reason: unknown) => {
          if (errorCode(reason) === 'VERSION_CONFLICT') {
            setError('다른 관리자가 브랜드 설정을 먼저 변경했습니다. 최신 설정을 다시 불러왔습니다.');
            resource.reload();
          } else setError(reason instanceof Error ? reason.message : '브랜드 테마를 저장하지 못했습니다.');
        },
      )
      .finally(() => setSaving(false));
  };
  return (
    <InformationPage
      title="모양"
      titleRef={location.titleRef}
      width="wide"
    >
      <ResourceState loading={resource.loading} error={resource.error?.message} empty={false} onRetry={resource.reload}>
        <PageSections>
          {!canWrite ? <Alert severity="warning">이 설정을 조회할 수 있지만 변경 권한은 없습니다.</Alert> : null}
          <AppearanceEditor
            draft={{
              ...draft,
              brandTheme: theme,
            }}
            baseline={baseline}
            resolvedTheme={resolved}
            action={
              canWrite ? (
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" color="inherit" disabled={!dirty || saving} onClick={() => {
                    if (saving) return;
                    setDraft((current) => ({ ...current, brandTheme: baseline.brandTheme }));
                    setError(undefined);
                    setNotice(undefined);
                  }}>취소</Button>
                  <Button variant="outlined" onClick={save} disabled={!dirty || !valid || saving}>
                    {saving ? '저장 중…' : '저장'}
                  </Button>
                </Stack>
              ) : undefined
            }
            error={error}
            canWrite={canWrite}
            saving={saving}
            onSelectPreset={selectPreset}
            onChangeSurfaceTone={(surfaceTone) => {
              setError(undefined);
              setNotice(undefined);
              setDraft((current) => ({ ...current, brandTheme: { ...current.brandTheme, surfaceTone: surfaceTone === 'cool' ? undefined : surfaceTone } }));
            }}
            onChangeColor={(field, value) => {
              setError(undefined);
              setNotice(undefined);
              setDraft((current) => ({
                ...current,
                brandTheme: {
                  ...current.brandTheme,
                  presetId: 'custom-v1',
                  [field]: value.toUpperCase(),
                },
              }));
            }}
          />
          <SectionCard
            help="로고는 헤더와 문서에 사용되며 확인 즉시 저장됩니다. 투명 배경의 PNG를 권장하며 가로형·정사각형 이미지를 모두 사용할 수 있습니다."
            bodyPadding="standard"
            showDivider={false}
            title={
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <ImageOutlined color="action" fontSize="small" />
                <span>회사 로고</span>
              </Stack>
            }
          >
            <Stack spacing={2.25}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'minmax(300px, 0.72fr) minmax(0, 1.28fr)',
                  },
                  gap: {
                    xs: 2,
                    sm: 3,
                  },
                  alignItems: 'center',
                }}
              >
                <OrganizationLogoControl
                  variant="inline"
                  port={port}
                  settings={baseline}
                  onNotice={setNotice}
                  fixtureMode={fixtureMode}
                  readOnly={!canWrite}
                  disabled={!canWrite || saving}
                  onChanged={(settings) => {
                    const normalized = normalizeOrganizationSettings(settings);
                    setBaseline(normalized);
                    setDraft((current) => ({
                      ...normalized,
                      displayName: current.displayName,
                      brandTheme: current.brandTheme,
                    }));
                    onOrganizationChanged(normalized);
                  }}
                />
                <Stack spacing={0.75}>
                  <Typography component="h3" sx={designTokens.typography.subsectionTitle}>헤더와 문서에 표시할 로고</Typography>
                </Stack>
              </Box>
            </Stack>
          </SectionCard>
        </PageSections>
      </ResourceState>
      <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice(undefined)} />
    </InformationPage>
  );
}
